import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { useAccountsService, useImprvApi } from '@hooks';
import { buildAPIUrl } from '@http';
import { supportsPushNotifications } from '@platform';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import * as Sentry from '@sentry/nextjs';
import { useStore } from '@store';
import { shallow } from 'zustand/shallow';

const NotificationsGuard: React.FC = ({ children }: { children: ReactNode }) => {
  const imprvApi = useImprvApi();
  const [permissionStatus, setPermissionStatus] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const setupInFlightRef = useRef(false);
  const setupDoneForUserRef = useRef<string | null>(null);
  const { setImprvApi } = useStore(
    (state) => ({
      setImprvApi: state.setImprvApi
    }),
    shallow
  );
  const { accountMe, getAccountMe } = useAccountsService();

  // BACKWARD COMPATIBILITY: this is a temporary solution to ensure that we're able to fetch
  // the token in the calls that are not integrated with the useImprvApi
  useEffect(() => {
    setImprvApi(imprvApi);
  }, []);

  useEffect(() => {
    if (!accountMe) {
      getAccountMe();
    }

    // Check current permission status
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }

    // Set up interaction detection for both environments
    const handleInteraction = () => {
      setHasInteracted(true);
      // Remove listener after first interaction
      document.removeEventListener('click', handleInteraction);
    };
    document.addEventListener('click', handleInteraction);
    return () => {
      document.removeEventListener('click', handleInteraction);
    };
  }, [accountMe, getAccountMe]);

  // Function to register service worker and set up notifications
  const setupPushNotifications = async () => {
    if (!accountMe) return;
    const userId = accountMe?.user?.id?.toString() ?? null;
    if (setupInFlightRef.current || setupDoneForUserRef.current === userId) return;
    setupInFlightRef.current = true;
    try {
      // First request notification permission from user
      if ('Notification' in window && Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        setPermissionStatus(permission);
        if (permission !== 'granted') {
          return; // Exit if permission not granted
        }
      }

      // Then register service worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register('/pusher-service-worker.js');
        console.log('Service worker registered successfully');

        if (!supportsPushNotifications()) {
          console.log('Push notifications not supported by browser');
          return;
        }

        const token = await imprvApi.getAPIToken();
        const beamsTokenProvider = new PusherPushNotifications.TokenProvider({
          url: buildAPIUrl('/notifications/token'),
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const beamsClient = new PusherPushNotifications.Client({
          instanceId: process.env.NEXT_PUBLIC_BEAMS_INSTANCE_ID,
          serviceWorkerRegistration: registration
        });

        // Check if user is already registered
        try {
          const beamsUserId = await beamsClient.getUserId();
          // If user ID doesn't match current user, stop current subscription
          if (beamsUserId !== accountMe?.user?.id.toString()) {
            await beamsClient.stop();
          }
        } catch (e) {
          // No existing user ID, which is fine for new registrations
        }

        // Start client and register user
        await beamsClient.start();

        // Add retry logic to ensure device registration is complete
        let deviceId = null;
        let retries = 0;
        const maxRetries = 3;

        while (!deviceId && retries < maxRetries) {
          try {
            deviceId = await beamsClient.getDeviceId();
            if (!deviceId) {
              retries++;
              // Wait 500ms between retries (with exponential backoff)
              await new Promise((resolve) => setTimeout(resolve, 500 * Math.pow(2, retries)));
              console.log(`Retry ${retries}/${maxRetries} for Pusher Beams device registration`);
            }
          } catch (error) {
            retries++;
            console.warn(`Error getting device ID (attempt ${retries}/${maxRetries}):`, error);
            // Wait before retrying
            await new Promise((resolve) => setTimeout(resolve, 500 * Math.pow(2, retries)));
          }
        }

        // Make sure we have a valid device ID before attempting to set user ID
        if (deviceId) {
          await beamsClient.setUserId(accountMe?.user?.id.toString(), beamsTokenProvider);
          console.log('Push notifications set up successfully');
          setupDoneForUserRef.current = userId;
        } else {
          console.error('Failed to get device ID from Pusher Beams after multiple attempts');
          Sentry.captureMessage('Pusher Beams device registration failed after multiple attempts');
        }
      }
    } catch (error) {
      console.error('Failed to set up push notifications:', error);
      // Add more context to the error for Sentry
      if (error.name === 'HttpError' && error.status === 404) {
        Sentry.captureException(
          new Error(`Pusher Beams device not found (404): ${error.message}`),
          {
            tags: {
              pusher_beams_error: 'device_not_found',
              instance_id: process.env.NEXT_PUBLIC_BEAMS_INSTANCE_ID
            }
          }
        );
      } else {
        Sentry.captureException(error, {
          tags: { component: 'NotificationsGuard' }
        });
      }
    } finally {
      setupInFlightRef.current = false;
    }
  };

  // Request permissions consistently in both environments
  useEffect(() => {
    // Auto-setup when permission is already granted
    if (accountMe && permissionStatus === 'granted') {
      setupPushNotifications();
    }
    // Request permission after user has interacted with the page, and only if default state
    else if (hasInteracted && accountMe && permissionStatus === 'default') {
      setupPushNotifications();
    }
  }, [accountMe, permissionStatus, hasInteracted]);

  return <>{children}</>;
};

export default NotificationsGuard;
